import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/posthog";
import { PLAN_LIMITS, planFromRow } from "@/lib/plans";

export interface Refinement {
  id: string;
  image_url: string;
  prompt_text: string;
  reference_image_url: string | null;
  variant_id: string | null;
  created_at: string;
}

/** Free-plan monthly iteration tokens; real limit is resolved per plan. */
export const REFINEMENT_LIMIT = PLAN_LIMITS.free.refines;

export function useRefinements(designId: string | undefined) {
  const [refinements, setRefinements] = useState<Refinement[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Iteration tokens are a monthly, per-user allowance across all designs.
  const [monthUsed, setMonthUsed] = useState(0);
  const [limit, setLimit] = useState<number>(REFINEMENT_LIMIT);

  const loadAllowance = useCallback(async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    const now = new Date();
    const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
    const [{ count }, { data: sub }] = await Promise.all([
      supabase.from("design_refinements").select("id", { count: "exact", head: true })
        .eq("user_id", u.user.id).gte("created_at", monthStart),
      supabase.from("subscribers").select("plan, status, current_period_end").eq("user_id", u.user.id).maybeSingle(),
    ]);
    setMonthUsed(count ?? 0);
    setLimit(PLAN_LIMITS[planFromRow(sub)].refines);
  }, []);

  useEffect(() => { void loadAllowance(); }, [loadAllowance]);

  const load = useCallback(async () => {
    if (!designId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("design_refinements")
      .select("id, image_url, prompt_text, reference_image_url, variant_id, created_at")
      .eq("design_id", designId)
      .order("created_at", { ascending: true });
    if (!error && data) setRefinements(data as Refinement[]);
    setLoading(false);
  }, [designId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(
    async (params: {
      promptText: string;
      referenceImageDataUrl?: string | null;
      baseImageUrl?: string | null;
      variantId?: string | null;
    }) => {
      if (!designId) throw new Error("Missing design id");
      setSubmitting(true);
      try {
        const { data, error } = await supabase.functions.invoke("refine-design", {
          body: {
            designId,
            promptText: params.promptText,
            referenceImageDataUrl: params.referenceImageDataUrl ?? undefined,
            baseImageUrl: params.baseImageUrl ?? undefined,
            variantId: params.variantId ?? undefined,
          },
        });
        if (error) {
          // Try to read structured error from the function response.
          const ctx = (error as any).context;
          let parsed: any = null;
          try {
            parsed = ctx ? await ctx.json() : null;
          } catch {
            // ignore
          }
          const message =
            parsed?.error === "refinement_limit_reached"
              ? "You've used all your iteration tokens for this month."
              : parsed?.error === "rate_limited"
              ? "Slow down a moment — try again shortly."
              : parsed?.error ?? error.message ?? "Refinement failed.";
          throw new Error(message);
        }
        const refinement = (data as any)?.refinement as Refinement | undefined;
        if (!refinement) {
          throw new Error((data as any)?.error || "Refinement returned no image.");
        }
        setRefinements((prev) => [...prev, refinement]);
        setMonthUsed((n) => n + 1);
        return refinement;
      } catch (err: any) {
        track("generation_failed", {
          design_id: designId,
          error_type: err?.message || "unknown",
          generation_type: "refine",
          is_regen: false,
          surface: "refine_client",
        });
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [designId],
  );

  const used = monthUsed;
  const remaining = Math.max(0, limit - used);

  return { refinements, loading, submitting, submit, used, remaining, limit, reload: load };
}
